import React from "react";
import DateInput from "./components/DateInput";
import DateTimeInput from "./components/DateTimeInput";
import RadioGroup from "./components/RadioGroup";
import SelectField from "./components/SelectField";
import TextareaField from "./components/TextareaField";
import CheckboxField from "./components/CheckboxField";
import NumberField from "./components/NumberField";
import TextField from "./components/TextField";
import { evaluate } from "mathjs"; // yarn add mathjs

// 생년월일 필드 (자동 나이 계산 포함)
export const DateWithAgeField = ({ field, value, onChange }) => {
  const calculateAge = (birthDate) => {
    if (!birthDate) return "";

    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();

    // 생일이 아직 지나지 않았으면 나이 -1
    if (
      today.getMonth() < birth.getMonth() ||
      (today.getMonth() === birth.getMonth() &&
        today.getDate() < birth.getDate())
    ) {
      age--;
    }

    return age;
  };

  return (
    <div className="date-with-age-field">
      <div className="input-field">
        <DateInput
          value={value || ""}
          onChange={(date) => onChange(field.field_id, date)}
        />
      </div>
      {value && (
        <div className="calculated-age">
          <span className="age-label">나이:</span>
          <span className="age-value">{calculateAge(value)}세</span>
        </div>
      )}
    </div>
  );
};

// 조건 체크 유틸리티 함수 (기존 코드에서 사용됨)
const checkCondition = (dependsOn, dependsOnValue, formData) => {
  // 의존하는 필드가 없으면 항상 표시
  if (!dependsOn) return true;

  // 조건 객체 형식 (복합 조건)
  if (
    typeof dependsOn === "object" &&
    !Array.isArray(dependsOn) &&
    dependsOn.hasOwnProperty("condition")
  ) {
    // AND 조건
    if (dependsOn.condition === "AND") {
      return dependsOn.fields.every((field) => {
        return checkFieldCondition(field.field, field.value, formData);
      });
    }
    // OR 조건
    else if (dependsOn.condition === "OR") {
      return dependsOn.fields.some((field) => {
        return checkFieldCondition(field.field, field.value, formData);
      });
    }
  }

  // 배열 형태 (여러 필드에 의존하는 경우)
  if (Array.isArray(dependsOn)) {
    // dependsOnValue가 함수라면 여러 필드 값을 한번에 검사하는 사용자 정의 함수
    if (typeof dependsOnValue === "function") {
      const fieldValues = {};
      dependsOn.forEach((fieldId) => {
        fieldValues[fieldId] = formData[fieldId];
      });
      return dependsOnValue(fieldValues);
    }

    // 기본적으로는 모든 필드에 값이 있는지 확인
    return dependsOn.every(
      (fieldId) => formData[fieldId] !== undefined && formData[fieldId] !== ""
    );
  }

  // 단일 필드 의존성 처리
  return checkFieldCondition(dependsOn, dependsOnValue, formData);
};

// 단일 필드 조건 체크 도우미 함수
const checkFieldCondition = (fieldId, expectedValue, formData) => {
  const fieldValue = formData[fieldId];

  // 함수 형태의 조건
  if (typeof expectedValue === "function") {
    return expectedValue(fieldValue);
  }

  // 배열 형태의 조건 - 여러 값 중 하나라도 일치하면 표시
  if (Array.isArray(expectedValue)) {
    return expectedValue.includes(fieldValue);
  }

  // 단일 값 조건 - 정확히 일치할 때 표시
  return fieldValue === expectedValue;
};

// 조건부 표시 필드
export const ConditionalField = ({
  field,
  value,
  dependsOn,
  dependsOnValue,
  onChange,
  formData,
}) => {
  // 변경된 조건 체크 함수 사용
  const shouldShow = () => checkCondition(dependsOn, dependsOnValue, formData);

  if (!shouldShow()) return null;

  return (
    <DynamicField
      field={field}
      value={value}
      onChange={onChange}
      formData={formData}
    />
  );
};

// 레이아웃 컴포넌트 - 단일 컬럼
export const SingleColumnLayout = ({ children, title, className = "" }) => {
  return (
    <div className={`input-wrapper-1column ${className}`}>
      <div className="block-1column">
        {title && <div className="item_title_1col">{title}</div>}
        <div className="input_1col">{children}</div>
      </div>
    </div>
  );
};

// 레이아웃 컴포넌트 - 두 컬럼
export const TwoColumnLayout = ({
  leftChild,
  rightChild,
  leftTitle,
  rightTitle,
  className = "",
}) => {
  return (
    <div className={`input-wrapper-2column ${className}`}>
      <div className="block-2column">
        {leftTitle && <div className="item_title_2col">{leftTitle}</div>}
        <div className="input_2col">{leftChild}</div>
      </div>
      <div className="block-2column">
        {rightTitle && <div className="item_title_2col">{rightTitle}</div>}
        <div className="input_2col">{rightChild}</div>
      </div>
    </div>
  );
};

// 조건부 섹션 컴포넌트
export const ConditionalSection = ({
  section,
  formData,
  handleChange,
  dependsOn,
  dependsOnValue,
}) => {
  // 변경된 조건 체크 함수 사용
  const shouldShow = () => checkCondition(dependsOn, dependsOnValue, formData);

  if (!shouldShow()) return null;

  return (
    <div className="form-section">
      <h3>{section.section_title}</h3>
      {section.fields &&
        section.fields.map((field) => {
          // 필드 렌더링 로직
          // 필드별 조건부 표시 로직 처리
          if (field.dependsOn) {
            return (
              <div key={field.field_id}>
                {field.layout === "full" ? (
                  <SingleColumnLayout title={field.label}>
                    <ConditionalField
                      field={field}
                      value={formData[field.field_id]}
                      dependsOn={field.dependsOn}
                      dependsOnValue={field.dependsOnValue}
                      onChange={handleChange}
                      formData={formData}
                    />
                  </SingleColumnLayout>
                ) : (
                  <div className="field-wrapper" key={field.field_id}>
                    <div className="item_title_2col">{field.label}</div>
                    <ConditionalField
                      field={field}
                      value={formData[field.field_id]}
                      dependsOn={field.dependsOn}
                      dependsOnValue={field.dependsOnValue}
                      onChange={handleChange}
                      formData={formData}
                    />
                  </div>
                )}
              </div>
            );
          }

          // 기본 필드 렌더링
          return (
            <div key={field.field_id}>
              {field.layout === "full" ? (
                <SingleColumnLayout title={field.label}>
                  <DynamicField
                    field={field}
                    value={formData[field.field_id]}
                    onChange={handleChange}
                    formData={formData}
                  />
                </SingleColumnLayout>
              ) : (
                <div className="field-wrapper">
                  <div className="item_title_2col">{field.label}</div>
                  <DynamicField
                    field={field}
                    value={formData[field.field_id]}
                    onChange={handleChange}
                    formData={formData}
                  />
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

// 수식 평가 & 형식 변환 유틸
const calcValue = (formula, formData, format = "number") => {
  try {
    const val = evaluate(formula || "0", formData);
    if (Number.isNaN(val) || val === Infinity) return "";
    switch (format) {
      case "days":
        return `${val} 일`;
      case "hours":
        return `${val} 시간`;
      case "minutes":
        return `${val} 분`;
      default:
        return val;
    }
  } catch (e) {
    return ""; // 수식 오류 시 빈값
  }
};

// 계산 필드 컴포넌트
export const CalculateField = ({ field, formData }) => {
  const value = calcValue(field.formula, formData, field.format);
  return <div className="calculated-value">{value}</div>;
};

// 일반 필드 컴포넌트 - 필드 타입에 따라 적절한 입력 요소 제공
export const DynamicField = ({ field, value, onChange, formData }) => {
  // 계산 필드를 위한 실시간 계산 처리
  let calculatedValue = value;
  if (field.type === "calculated" && field.formula) {
    calculatedValue = field.formula(formData);

    // 캐싱된 값과 다른 경우에만 업데이트 (불필요한 재렌더링 방지)
    if (value !== calculatedValue) {
      // setTimeout을 사용하여 렌더링 주기 밖에서 상태 업데이트
      setTimeout(() => {
        onChange(field.field_id, calculatedValue);
      }, 0);
    }
  }

  switch (field.type) {
    case "text":
      return (
        <TextField
          value={value || ""}
          onChange={(text) => onChange(field.field_id, text)}
          placeholder={field.placeholder || ""}
          required={field.required}
        />
      );

    case "number":
      return (
        <NumberField
          value={value || ""}
          onChange={(num) => onChange(field.field_id, num)}
          min={field.min}
          max={field.max}
          step={field.step || 1}
          placeholder={field.placeholder || ""}
          required={field.required}
          unit={field.unit}
        />
      );

    case "date":
      if (field.showAge) {
        return (
          <DateWithAgeField field={field} value={value} onChange={onChange} />
        );
      }
      return (
        <DateInput
          value={value || ""}
          onChange={(date) => onChange(field.field_id, date)}
        />
      );

    case "datetime":
      return (
        <DateTimeInput
          value={value || ""}
          onChange={(datetime) => onChange(field.field_id, datetime)}
        />
      );

    case "select":
      return (
        <SelectField
          value={value || ""}
          onChange={(selected) => onChange(field.field_id, selected)}
          options={field.options || []}
          required={field.required}
        />
      );

    case "radio":
      return (
        <RadioGroup
          name={field.field_id}
          value={value || ""}
          onChange={(selected) => onChange(field.field_id, selected)}
          options={field.options || []}
          required={field.required}
        />
      );

    case "checkbox":
      return (
        <CheckboxField
          checked={value === true || value === "yes"}
          onChange={(checked) =>
            onChange(field.field_id, checked ? "yes" : "no")
          }
          label={field.checkboxLabel || field.label}
          required={field.required}
        />
      );

    case "textarea":
      return (
        <TextareaField
          value={value || ""}
          onChange={(text) => onChange(field.field_id, text)}
          placeholder={field.placeholder || ""}
          rows={field.rows || 4}
          required={field.required}
        />
      );

    case "calculated":
      return <CalculateField field={field} formData={formData} />;

    case "timediff":
      return <TimeDiffField field={field} formData={formData} />;

    default:
      return <div>지원되지 않는 필드 타입: {field.type}</div>;
  }
};

// ---- TimeDiffField ----
const diffMinutes = (a, b) => Math.floor((a - b) / (1000 * 60));

export const TimeDiffField = ({ field, formData }) => {
  const start = new Date(formData[field.startField]);
  const end = new Date(formData[field.endField]);
  if (isNaN(start) || isNaN(end)) return <span>-</span>;
  let minutes = diffMinutes(end, start);
  let display = minutes;
  switch (field.format) {
    case "hours":
      display = (minutes / 60).toFixed(1);
      break;
    case "days":
      display = (minutes / 60 / 24).toFixed(1);
      break;
    default:
      break;
  }
  return (
    <input type="text" className="form-control" value={display} readOnly />
  );
};
